import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { CreateDriverRequest, UpdateDriverRequest, triplePSDatasource } from "../../datasource";
import {
  APIResult,
  emptyAPIResult,
  loadingAPIResult,
  successAPIResult,
  errorAPIResult,
} from "../../lib/redux";
import { DriverV2, DailyProgress, PunchCardsForDriver, VehicleLocation } from "../../models";
import { logout } from "../Auth/authSlice";

export interface DriversV2State {
  getDrivers: APIResult<DriverV2[]>;
  getDriver: APIResult<DriverV2>;
  createDriver: APIResult<DriverV2>;
  updateDriver: APIResult<DriverV2>;
  deleteDriver: APIResult<string>;
  getDailyProgress: APIResult<DailyProgress>;
  getPunchCardsForDriver: APIResult<PunchCardsForDriver>;
  getDriverLocation: APIResult<{ location?: VehicleLocation; name?: string }>;

  drivers: DriverV2[];
  selectedDriver?: DriverV2;
  showDeleteDriverConfirmationModal?: DriverV2;
  driverToRemove?: string;
  showAddDriverModal: boolean;
  showEditDriverModal: boolean;
  dailyProgress?: DailyProgress;
  punchCardsForDriver?: PunchCardsForDriver;
  driverLocation?: VehicleLocation;
  driverLocationName?: string;
}

const initialState: DriversV2State = {
  getDrivers: emptyAPIResult(),
  getDriver: emptyAPIResult(),
  createDriver: emptyAPIResult(),
  updateDriver: emptyAPIResult(),
  deleteDriver: emptyAPIResult(),
  getDailyProgress: emptyAPIResult(),
  getPunchCardsForDriver: emptyAPIResult(),
  getDriverLocation: emptyAPIResult(),

  drivers: [],
  showAddDriverModal: false,
  showEditDriverModal: false,
};

export const getDriversV2 = createAsyncThunk(
  "driversV2/getDrivers",
  async () => {
    return triplePSDatasource.getDriversV2();
  }
);

export const getDriverV2 = createAsyncThunk(
  "driversV2/getDriver",
  async (id: string) => {
    return triplePSDatasource.getDriverV2(id);
  }
);

export const createDriverV2 = createAsyncThunk(
  "driversV2/createDriver",
  async (request: CreateDriverRequest) => {
    return triplePSDatasource.createDriverV2(request);
  }
);

export const updateDriverV2 = createAsyncThunk(
  "driversV2/updateDriver",
  async (params: { id: string; request: UpdateDriverRequest }) => {
    return triplePSDatasource.updateDriverV2(params.id, params.request);
  }
);

export const deleteDriverV2 = createAsyncThunk(
  "driversV2/deleteDriver",
  async (id: string) => {
    return triplePSDatasource.deleteDriverV2(id);
  }
);

export const getDailyProgressV2 = createAsyncThunk(
  "driversV2/getDailyProgress",
  async (params: { date: string; userId: string }) => {
    return triplePSDatasource.getDailyProgress(params.date, params.userId);
  }
);

export const getPunchCardsForDriverV2 = createAsyncThunk(
  "driversV2/getPunchCardsForDriver",
  async (params: { driverId: string; fromDate: string; toDate: string }) => {
    return triplePSDatasource.getPunchCardsForDriver(params);
  }
);

export const getDriverLocationV2 = createAsyncThunk(
  "driversV2/getDriverLocation",
  // driverId is the drivers-table PK (DriverV2.id), NOT the user_id — GET
  // /drivers/{id} filters on drivers.id, so a user_id 404s here.
  async (params: { driverId: string }) => {
    const driver = await triplePSDatasource.getDriverV2(params.driverId, true);
    // Carry the name so the map popup is self-contained and doesn't depend on
    // selectedDriver being populated for whichever route opened this tab.
    return { location: driver.location, name: driver.name };
  }
);

export const driversV2Slice = createSlice({
  name: "driversV2",
  initialState,
  reducers: {
    setDriverToRemove(state, action) {
      state.driverToRemove = action.payload;
    },
    setSelectedDriver(state, action) {
      state.selectedDriver = action.payload;
    },
    toggleShowAddDriverModal(state) {
      state.showAddDriverModal = !state.showAddDriverModal;
    },
    toggleShowEditDriverModal(state) {
      state.showEditDriverModal = !state.showEditDriverModal;
    },
    showDeleteDriverConfirmationModal(state, action) {
      state.showDeleteDriverConfirmationModal = action.payload;
    },
    resetShowDeleteDriverConfirmationModal(state) {
      state.showDeleteDriverConfirmationModal = undefined;
    },
    resetUpdateDriver(state) {
      state.updateDriver = emptyAPIResult();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDriversV2.pending, (state) => {
        state.getDrivers = loadingAPIResult();
      })
      .addCase(getDriversV2.fulfilled, (state, action) => {
        state.getDrivers = successAPIResult(action.payload);
        state.drivers = action.payload;
      })
      .addCase(getDriversV2.rejected, (state, action) => {
        state.getDrivers = errorAPIResult(action.error);
      })
      .addCase(getDriverV2.pending, (state) => {
        state.getDriver = loadingAPIResult();
      })
      .addCase(getDriverV2.fulfilled, (state, action) => {
        state.getDriver = successAPIResult(action.payload);
        state.selectedDriver = action.payload;
      })
      .addCase(getDriverV2.rejected, (state, action) => {
        state.getDriver = errorAPIResult(action.error);
      })
      .addCase(createDriverV2.pending, (state) => {
        state.createDriver = loadingAPIResult();
      })
      .addCase(createDriverV2.fulfilled, (state, action) => {
        state.createDriver = successAPIResult(action.payload);
        state.drivers.push(action.payload);
        state.showAddDriverModal = false;
      })
      .addCase(createDriverV2.rejected, (state, action) => {
        state.createDriver = errorAPIResult(action.error);
      })
      .addCase(updateDriverV2.pending, (state) => {
        state.updateDriver = loadingAPIResult();
      })
      .addCase(updateDriverV2.fulfilled, (state, action) => {
        state.updateDriver = successAPIResult(action.payload);
        const index = state.drivers.findIndex(
          (d) => d.id === action.payload.id
        );
        if (index !== -1) {
          state.drivers[index] = action.payload;
        }
        state.selectedDriver = action.payload;
        state.showEditDriverModal = false;
      })
      .addCase(updateDriverV2.rejected, (state, action) => {
        state.updateDriver = errorAPIResult(action.error);
      })
      .addCase(deleteDriverV2.pending, (state) => {
        state.deleteDriver = loadingAPIResult();
      })
      .addCase(deleteDriverV2.fulfilled, (state, action) => {
        state.deleteDriver = successAPIResult(action.payload);
        state.drivers = state.drivers
          ? state.drivers.filter((d) => d.id !== action.payload)
          : [];
        state.driverToRemove = undefined;
        state.showDeleteDriverConfirmationModal = undefined;
      })
      .addCase(deleteDriverV2.rejected, (state, action) => {
        state.deleteDriver = errorAPIResult(action.error);
      })
      .addCase(getDailyProgressV2.pending, (state) => {
        state.getDailyProgress = loadingAPIResult();
      })
      .addCase(getDailyProgressV2.fulfilled, (state, action) => {
        state.getDailyProgress = successAPIResult(action.payload);
        state.dailyProgress = action.payload;
      })
      .addCase(getDailyProgressV2.rejected, (state, action) => {
        state.getDailyProgress = errorAPIResult(action.error);
      })
      .addCase(getPunchCardsForDriverV2.pending, (state) => {
        state.getPunchCardsForDriver = loadingAPIResult();
      })
      .addCase(getPunchCardsForDriverV2.fulfilled, (state, action) => {
        state.getPunchCardsForDriver = successAPIResult(action.payload);
        state.punchCardsForDriver = action.payload;
      })
      .addCase(getPunchCardsForDriverV2.rejected, (state, action) => {
        state.getPunchCardsForDriver = errorAPIResult(action.error);
      })
      .addCase(getDriverLocationV2.pending, (state) => {
        state.getDriverLocation = loadingAPIResult();
      })
      .addCase(getDriverLocationV2.fulfilled, (state, action) => {
        state.getDriverLocation = successAPIResult(action.payload);
        state.driverLocation = action.payload.location;
        state.driverLocationName = action.payload.name;
      })
      .addCase(getDriverLocationV2.rejected, (state, action) => {
        state.getDriverLocation = errorAPIResult(action.error);
      })
      .addMatcher(isAnyOf(logout), (state) => {
        state.getDrivers = initialState.getDrivers;
        state.getDriver = initialState.getDriver;
        state.createDriver = initialState.createDriver;
        state.updateDriver = initialState.updateDriver;
        state.deleteDriver = initialState.deleteDriver;
        state.getDailyProgress = initialState.getDailyProgress;
        state.getPunchCardsForDriver = initialState.getPunchCardsForDriver;
        state.getDriverLocation = initialState.getDriverLocation;

        state.drivers = initialState.drivers;
        state.selectedDriver = undefined;
        state.driverToRemove = undefined;
        state.showAddDriverModal = initialState.showAddDriverModal;
        state.showEditDriverModal = initialState.showEditDriverModal;
        state.showDeleteDriverConfirmationModal = undefined;
        state.dailyProgress = undefined;
        state.punchCardsForDriver = undefined;
        state.driverLocation = undefined;
        state.driverLocationName = undefined;
      });
  },
});

export const {
  setDriverToRemove,
  setSelectedDriver,
  toggleShowAddDriverModal,
  toggleShowEditDriverModal,
  showDeleteDriverConfirmationModal,
  resetShowDeleteDriverConfirmationModal,
  resetUpdateDriver,
} = driversV2Slice.actions;

export default driversV2Slice.reducer;
